import { Link } from "react-router-dom";
import {
    ArrowRight,
    ArrowDown,
    SplitSquareVertical,
    ShieldCheck,
    Calculator,
    Lock,
    ScanSearch,
    Cpu,
    Gauge,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import InstrumentPanel from "@/components/InstrumentPanel";
import EarlyAccessForm from "@/components/EarlyAccessForm";
import {
    Container,
    Section,
    Eyebrow,
    RuleDivider,
    SectionHeading,
    AmberButton,
    GhostButton,
    Chip,
    CardImage,
} from "@/components/Primitives";
import { TRUST_STRIP } from "@/data/site";

const PILLARS = [
    {
        icon: ShieldCheck,
        title: "The aegis holds the process.",
        body: "The methodology doesn't bend under pressure. New ideas are tested on sessions the system never tuned against. A change justified by \u201cthis would have caught that trade\u201d isn't a change \u2014 it's a chase. We stop there.",
        to: "/methodology",
        img: "/images/cards/proven.png",
    },
    {
        icon: SplitSquareVertical,
        title: "Separation of powers.",
        body: "Deterministic code computes every signal. A reasoning model makes the trade decision. Neither does the other's job \u2014 so neither can hide behind the other when it goes wrong.",
        to: "/how-it-works",
        img: "/images/cards/separation.png",
    },
    {
        icon: Lock,
        title: "The desk never enters the trader's room.",
        body: "The model receives clean, structural facts \u2014 never a pre-baked verdict. No \u201cthis is a strong setup\u201d labels nudging the call. The analysis informs. It never decides.",
        to: "/methodology",
        img: "/images/cards/desk_room.png",
    },
    {
        icon: Calculator,
        title: "Risk is computed, not felt.",
        body: "Stops scale with each instrument's own volatility. Position size scales with conviction. The rules hold the same way on a bad day as on a good one.",
        to: "/platform",
        img: "/images/cards/risk_computed.png",
    },
];

const STEPS = [
    {
        icon: ScanSearch,
        n: "1",
        title: "Screen",
        body: "Every morning, The Aegis Line scans the market for names that earned their place today: stocks gapping on genuine volume with a real catalyst, and index futures setting up against their overnight range.",
        img: "/images/cards/screen_market.png",
    },
    {
        icon: Cpu,
        n: "2",
        title: "Analyze",
        body: "Each candidate runs through a stack of deterministic analysis modules \u2014 trend, momentum, volume, relative strength, the opening range. Code. Same inputs, same outputs, every time.",
        img: "/images/cards/compute_structure.png",
    },
    {
        icon: Gauge,
        n: "3",
        title: "Decide & manage",
        body: "A reasoning model reads the clean structural brief and makes the call \u2014 enter, wait, or pass \u2014 then manages the position candle by candle, with every decision written down.",
        img: "/images/cards/decide_manage.png",
    },
];

export default function Home() {
    return (
        <>
            <Seo description="The Aegis Line screens the market, computes the structure, and lets a reasoning model make the call — across stocks and futures — with every decision held to a standard that doesn't bend for a single trade." />

            {/* Section 1 — Hero */}
            <section className="crosshair-bg relative overflow-hidden">
                <Container className="grid items-center gap-12 py-20 sm:py-28 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
                    <Reveal>
                        <Eyebrow className="mb-6 block">
                            AI day-trading engine · Stocks &amp; Futures
                        </Eyebrow>
                        <h1 className="text-5xl font-extrabold leading-[1.04] text-text-hi sm:text-6xl lg:text-7xl">
                            The line holds.
                        </h1>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-text-lo sm:text-lg">
                            The Aegis Line screens the market, computes the
                            structure, and lets a reasoning model make the call
                            — across stocks and futures — with every decision
                            held to a standard that doesn't move for a single
                            trade.
                        </p>

                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <AmberButton
                                to="/#early-access"
                                testId="hero-primary-cta"
                            >
                                Get early access <ArrowRight size={16} />
                            </AmberButton>
                            <a
                                href="#how-it-works"
                                data-testid="hero-secondary-cta"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-text-hi transition-colors hover:text-rule"
                            >
                                See how it works <ArrowDown size={16} />
                            </a>
                        </div>

                        <div className="mt-10">
                            <p className="font-mono text-[12px] tracking-wide text-text-lo">
                                {TRUST_STRIP}
                            </p>
                            <p className="mt-2 font-mono text-[11px] text-rule/90">
                                Currently in paper-trading evaluation. We'll tell
                                you exactly when that changes.
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <InstrumentPanel />
                    </Reveal>
                </Container>
            </section>

            <Container>
                <RuleDivider />
            </Container>

            {/* Section 2 — The problem */}
            <Section>
                <Container>
                    <Reveal>
                        <div className="max-w-3xl">
                            <Eyebrow className="mb-5 block">The problem</Eyebrow>
                            <h2 className="text-3xl leading-tight text-text-hi sm:text-4xl lg:text-5xl">
                                Most traders don't lose because they lack
                                knowledge.
                            </h2>
                            <p className="mt-6 text-base leading-relaxed text-text-lo sm:text-lg">
                                They lose because nothing holds them to a
                                standard when it matters most. They move the
                                stop because the trade <span className="italic">feels</span> like it
                                should work. They chase the entry because
                                missing it hurts. They tweak the system after
                                every session to catch the trades that got away
                                — and mistake that for improvement. The line
                                doesn't hold because there's nothing protecting
                                it. The Aegis Line was built to be that
                                protection.
                            </p>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* Section 3 — Four pillars */}
            <Section className="border-y border-haze bg-ink-soft/40">
                <Container>
                    <Reveal>
                        <SectionHeading
                            eyebrow="The four pillars"
                            title="Four ways the line holds."
                            sub="Each pillar maps to a real mechanic in the system — not a slogan."
                        />
                    </Reveal>
                    <div className="mt-14 grid gap-5 sm:grid-cols-2">
                        {PILLARS.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.08}>
                                <Link
                                    to={p.to}
                                    data-testid={`pillar-card-${i}`}
                                    className="group block h-full overflow-hidden rounded-lg border border-haze bg-ink-soft transition-colors hover:border-rule/50"
                                >
                                    <CardImage
                                        src={p.img}
                                        alt={p.title}
                                        className="h-40 w-full border-b border-haze"
                                    />
                                    <div className="p-7">
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-10 w-10 items-center justify-center rounded border border-haze text-rule">
                                            <p.icon size={18} />
                                        </span>
                                        <span className="font-mono text-xs text-text-lo">
                                            0{i + 1}
                                        </span>
                                    </div>
                                    <h3 className="mt-5 text-lg font-semibold text-text-hi">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-text-lo">
                                        {p.body}
                                    </p>
                                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-rule opacity-0 transition-opacity group-hover:opacity-100">
                                        Learn more <ArrowRight size={14} />
                                    </span>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </Section>

            {/* Section 4 — How it works condensed */}
            <Section id="how-it-works">
                <Container>
                    <Reveal>
                        <SectionHeading
                            eyebrow="How it works"
                            title="The same path a disciplined desk would take."
                        />
                    </Reveal>
                    <div className="mt-14 grid gap-6 md:grid-cols-3">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.title} delay={i * 0.1}>
                                <div className="group h-full overflow-hidden rounded-lg border border-haze bg-ink-soft transition-colors hover:border-rule/40">
                                    <CardImage
                                        src={s.img}
                                        alt={s.title}
                                        className="h-40 w-full border-b border-haze"
                                    />
                                    <div className="p-7">
                                    <div className="flex items-center justify-between">
                                        <span className="flex h-10 w-10 items-center justify-center rounded border border-haze text-rule">
                                            <s.icon size={18} />
                                        </span>
                                        <span className="font-mono text-3xl font-semibold text-haze">
                                            {s.n}
                                        </span>
                                    </div>
                                    <h3 className="mt-5 text-lg font-semibold text-text-hi">
                                        {s.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-text-lo">
                                        {s.body}
                                    </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <div className="mt-10">
                            <GhostButton
                                to="/how-it-works"
                                testId="home-pipeline-cta"
                            >
                                See the full pipeline <ArrowRight size={16} />
                            </GhostButton>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* Section 5 — Two markets */}
            <Section className="border-y border-haze bg-ink-soft/40">
                <Container className="grid items-center gap-12 lg:grid-cols-2">
                    <Reveal>
                        <Eyebrow className="mb-5 block">Two markets, one engine</Eyebrow>
                        <h2 className="text-3xl leading-tight text-text-hi sm:text-4xl">
                            Stocks at the open. Futures at the open.
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-text-lo">
                            The same disciplined engine, built for two arenas.
                            For stocks, The Aegis Line targets low-float
                            gap-and-go setups in the first hour — the highest
                            energy, highest-structure window of the trading day.
                            For futures, it trades acceptance of the overnight
                            and opening range on the index micros. Same
                            standard, same transparency, instrument-appropriate
                            rules.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            <Chip>Gap-and-go stocks</Chip>
                            <Chip tone="signal">MES + MNQ futures</Chip>
                            <Chip tone="lo">First-hour focus</Chip>
                        </div>
                        <div className="mt-8">
                            <GhostButton to="/markets" testId="home-markets-cta">
                                Explore the markets <ArrowRight size={16} />
                            </GhostButton>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <img
                            src="/images/markets_stocks.png"
                            alt="Abstract gap-and-go candlestick chart with a gold ruled level line"
                            className="w-full rounded-lg border border-haze"
                            loading="lazy"
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* Section 6 — Methodology teaser */}
            <Section>
                <Container className="grid items-center gap-12 lg:grid-cols-[0.95fr_1.05fr]">
                    <Reveal>
                        <img
                            src="/images/methodology_abstract.png"
                            alt="Abstract chart comparing held-out sessions against a baseline"
                            className="w-full rounded-lg border border-haze"
                            loading="lazy"
                        />
                    </Reveal>
                    <Reveal delay={0.12}>
                        <Eyebrow className="mb-5 block">Methodology</Eyebrow>
                        <h2 className="text-3xl leading-tight text-text-hi sm:text-4xl">
                            The aegis is the methodology.
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-text-lo">
                            The discipline protecting this system isn't a feature
                            — it's the whole product. No tuning on watched
                            sessions. No changes justified by a single missed
                            trade. Edge proven against a dumb baseline before
                            it's trusted. Paper results labelled as paper. The
                            line holds because the standard behind it never
                            moves.
                        </p>
                        <div className="mt-8">
                            <GhostButton
                                to="/methodology"
                                testId="home-methodology-cta"
                            >
                                Read our methodology <ArrowRight size={16} />
                            </GhostButton>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* Section 7 — Final CTA band */}
            <section
                id="early-access"
                className="border-t border-haze bg-ink-soft"
            >
                <Container className="py-20 sm:py-28">
                    <Reveal>
                        <div className="mx-auto max-w-2xl text-center">
                            <Eyebrow className="mb-5 block">Early access</Eyebrow>
                            <h2 className="text-3xl leading-tight text-text-hi sm:text-4xl lg:text-5xl">
                                Built for traders who need the line to hold.
                            </h2>
                            <p className="mt-5 text-base leading-relaxed text-text-lo sm:text-lg">
                                Join the early-access list. We'll tell you
                                exactly where the system stands — and exactly
                                when paper becomes live.
                            </p>
                            <div className="mt-8 flex justify-center">
                                <EarlyAccessForm />
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </section>
        </>
    );
}
